import React from 'react'
import { 
  FiX, 
  FiUser, 
  FiMail, 
  FiTag, 
  FiSave,
  FiLoader,
  FiAlertCircle,
  FiEdit2,
  FiUserCheck,
  FiAward,
  FiBookOpen,
  FiBriefcase,
  FiStar,
  FiCalendar
} from "react-icons/fi";
import { 
  MdOutlineClose, 
  MdOutlinePerson,
  MdOutlineEmail,
  MdOutlineCategory,
  MdOutlineSave,
  MdOutlineEdit,
  MdOutlineBook,
  MdOutlineAward,
  MdOutlineWork,
  MdOutlineStar,
  MdOutlineCalendarToday
} from "react-icons/md";
import { IoPersonOutline, IoMailOutline, IoPricetagOutline, IoSaveOutline } from "react-icons/io5";
import InputField from '../../../components/ui/Input'

const EditModal = ({ 
  onCancel, 
  onSubmit, 
  title, 
  EditData, 
  handleONChange,
  loading = false 
}) => {
  // Determine if this is editing a learner or expert
  const isLearner = EditData?.email !== undefined;
  const isExpert = EditData?.skill !== undefined;
  
  // Check if form is valid
  const isFormValid = EditData?.fullName?.trim() && 
    (isLearner ? EditData?.email?.trim() : EditData?.skill?.trim());

  // Get appropriate icon
  const getHeaderIcon = () => {
    if (isExpert) return FiAward;
    if (isLearner) return FiUserCheck;
    return FiEdit2;
  };
  const HeaderIcon = getHeaderIcon();

  // Get appropriate label for second field
  const getSecondFieldLabel = () => {
    if (isLearner) return 'Email';
    if (isExpert) return 'Skill';
    return 'Email / Skill';
  };

  // Get appropriate name for second field
  const getSecondFieldName = () => {
    if (isLearner) return 'email';
    if (isExpert) return 'skill';
    return 'email';
  };

  // Get appropriate value for second field
  const getSecondFieldValue = () => {
    if (isLearner) return EditData?.email || '';
    if (isExpert) return EditData?.skill || '';
    return '';
  };

  // Get appropriate icon for second field
  const getSecondFieldIcon = () => {
    if (isLearner) return FiMail;
    if (isExpert) return FiTag;
    return FiTag;
  };
  const SecondFieldIcon = getSecondFieldIcon();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#855DF6]/10 to-[#715EFE]/10 rounded-xl">
              <HeaderIcon className="w-5 h-5 text-[#715EFE]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">{title || 'Edit Profile'}</h3>
              <p className="text-xs text-gray-400 hidden sm:block">
                {isExpert ? 'Update expert details' : 'Update learner details'}
              </p>
            </div>
          </div>
          <button
            aria-label="Close modal"
            className="flex items-center justify-center w-9 h-9 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Full Name */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <FiUser className="w-4 h-4 text-gray-400" />
              <span>Full Name</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <IoPersonOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="fullName"
                value={EditData?.fullName || ""}
                onChange={handleONChange}
                placeholder="Enter full name..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* Email or Skill */}
          <div>
            <label className="flex items-center gap-2 mb-1.5 text-sm font-medium text-gray-700">
              <SecondFieldIcon className="w-4 h-4 text-gray-400" />
              <span>{getSecondFieldLabel()}</span>
              <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              {isLearner ? (
                <IoMailOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              ) : (
                <IoPricetagOutline className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              )}
              <input
                type={isLearner ? "email" : "text"}
                name={getSecondFieldName()}
                value={getSecondFieldValue()}
                onChange={handleONChange}
                placeholder={isLearner ? "Enter email address..." : "Enter skill..."}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#715EFE]/20 focus:border-[#715EFE] transition-all duration-200"
              />
            </div>
          </div>

          {/* User Type Badge */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <span className="text-xs font-medium text-gray-500">User Type:</span>
            <span className={`
              inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border
              ${isExpert 
                ? 'text-purple-700 bg-purple-50 border-purple-200' 
                : 'text-blue-700 bg-blue-50 border-blue-200'
              }
            `}>
              {isExpert ? (
                <>
                  <FiAward className="w-3.5 h-3.5" />
                  Expert
                </>
              ) : (
                <>
                  <FiUserCheck className="w-3.5 h-3.5" />
                  Learner
                </>
              )}
            </span>
          </div>

          {/* Form Validity Indicator */}
          {!isFormValid && (
            <div className="flex items-center gap-3 p-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl animate-in fade-in duration-200">
              <FiAlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
              <p>Please fill in all required fields</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0 bg-gray-50/50 rounded-b-2xl">
          <button
            className="w-full sm:w-auto px-6 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-200 ${
              isFormValid && !loading
                ? 'bg-gradient-to-r from-[#855DF6] to-[#715EFE] hover:shadow-lg hover:shadow-[#715EFE]/25 hover:-translate-y-0.5'
                : 'bg-gray-300 cursor-not-allowed opacity-70'
            }`}
            onClick={onSubmit}
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <>
                <FiLoader className="w-4 h-4 animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4" />
                <span>Edit Profile</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditModal